import { useEffect } from 'react';

const SITE = 'https://rovera.app';

function setMeta(selector: string, content: string) {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(selector.includes('link') ? 'href' : 'content', content);
}

/**
 * Per-page title/description/canonical/OG overrides. There's no SSR here —
 * index.html ships one static set of tags for the landing page (what social
 * unfurl bots see, since they don't run JS), and this updates the same tags
 * client-side for the page actually being viewed, which is enough for
 * Lighthouse's SEO audit and for JS-executing crawlers like Googlebot.
 */
export function Seo({
  title,
  description,
  path,
}: {
  title: string;
  description: string;
  path: string;
}) {
  useEffect(() => {
    const url = `${SITE}${path}`;
    document.title = title;
    setMeta('meta[name="description"]', description);
    setMeta('link[rel="canonical"]', url);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
  }, [title, description, path]);

  return null;
}

/** Injects a JSON-LD script tag for the lifetime of the mounted page,
 *  removing it on unmount so the next page's structured data doesn't stack. */
export function JsonLd({ data }: { data: object }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [data]);

  return null;
}
